import { useEffect, useState } from "react";

import { SpinnerOutlined } from "@aircall/icons";
import { Icon, Spacer, Typography } from "@aircall/tractor";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { DynamicLoaderProps } from ".";

const animation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

function* loadingTranslationKeysGenerator<TKey extends string>(
  translationsKeys: TKey[]
): Generator<TKey, TKey> {
  const texts: TKey[] = translationsKeys;
  let i = 0;

  while (true) {
    // Loops back when "i" exceeds length.
    yield texts[i % texts.length];
    i += 1;
  }
}

export function DynamicLoader<TKey extends string>({
  translationKeys,
  intervalTime = 3000,
  children,
  ...spacerProps
}: DynamicLoaderProps<TKey>) {
  const { t } = useTranslation();

  const [generator] = useState(() =>
    loadingTranslationKeysGenerator(translationKeys)
  );
  const [translationKey, setTranslationKey] = useState<TKey>(
    () => generator.next().value
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTranslationKey(generator.next().value);
    }, intervalTime);

    return () => {
      clearInterval(intervalId);
    };
  }, [setTranslationKey, generator, intervalTime]);

  return (
    <Spacer
      fluid
      direction="vertical"
      gap="xxs"
      alignItems="center"
      data-test="dynamic-loader"
      {...spacerProps}
    >
      {children ?? <Icon component={SpinnerOutlined} spin />}
      {/* "wait" means wait for previous exit animation to be over. */}
      <AnimatePresence mode="wait">
        <motion.div
          key={translationKey}
          initial={animation.initial}
          animate={animation.animate}
          exit={animation.exit}
        >
          <Typography variant="bodyMediumM">{t(translationKey)}</Typography>
        </motion.div>
      </AnimatePresence>
    </Spacer>
  );
}
